import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { NewRiskType } from "../NewRiskDialog";
import { Icons, norm, type IconFn } from "./riskIcons";
import { VermoegensMatrix, EBENEN, type Ebene } from "./VermoegensMatrix";
import AnlagenImportDialog from "./AnlagenImportDialog";
import {
  ObjektmappenTeil,
  PlusKnopf,
  objektmappenBefund,
  useObjektmappen,
} from "./ObjektmappenSection";
import { ErtragsausfallSection } from "./ErtragsausfallSection";
import { OrganigrammPopup, type Verwaltung } from "../redesign/OrganigrammPopup";
import { ImmobilienmappenSection } from "./ImmobilienmappenSection";
import { istImmobilienVerbund } from "./immobilienVerbund";
import { IMMO_CSS_VARS } from "./immoTokens";
import {
  useCompanyNetwork,
  type CompanyNetworkFirma,
} from "../../services/companyNetworkService";
import { useBrancheClassification } from "../../data/brancheClassification";
import { brancheIcon, unternehmensfunktionIcon, IconRahmen } from "../../data/brancheIcons";
import { branchenAus, funktionenAus } from "./firmenStammdaten";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openRiskFormWindow } from "../windowManager/RiskFormWindow";
import { vorgangService, VorgangDto } from "../../services/vorgangService";
import {
  risikoobjektLabel,
  type RiskObjectLite,
} from "./risikoobjektLabel";

/* ──────────────────────────────────────────────────────────────────────────
   Types
   ────────────────────────────────────────────────────────────────────────── */

type FullRiskObject = RiskObjectLite & {
  id: number;
  contactID?: number;
  contractID?: number | null;
  contract?: { id: number; versicherungsnummer?: string } | null;
};

/* ──────────────────────────────────────────────────────────────────────────
   Categories
   ────────────────────────────────────────────────────────────────────────── */

/** Box 2 in section B — "Spezielle Risikoobjekte" (count). */
type RisikoobjektTile = {
  key: string;
  label: string;
  color: string;
  Icon: IconFn;
  match: (riskType: string) => boolean;
  /** True if no form exists yet — tile is rendered as Platzhalter. */
  placeholder?: boolean;
};

const RISIKOOBJEKTE: RisikoobjektTile[] = [
  {
    key: "immo",
    label: "Immobilien",
    color: "#0f4c81",
    Icon: Icons.house,
    match: (t) => norm(t).includes("immo"),
  },
  {
    key: "kfz",
    label: "KFZ",
    color: "#b91c1c",
    Icon: Icons.car,
    match: (t) => norm(t).includes("kfz"),
  },
  {
    key: "maschinen",
    label: "Maschinen",
    color: "#7c5a3a",
    Icon: Icons.gear,
    match: (t) => norm(t).includes("maschin") && !norm(t).includes("selbstfahr"),
  },
  {
    key: "selbstfahrend",
    label: "Selbstfahrende Maschinen",
    color: "#854d0e",
    Icon: Icons.bulldozer,
    match: (t) => norm(t).includes("selbstfahr"),
  },
  {
    key: "umwelt",
    label: "Umwelt",
    color: "#15803d",
    Icon: Icons.oilTank,
    match: (t) => norm(t).includes("umwelt"),
  },
  {
    key: "photovoltaik",
    label: "Photovoltaik",
    color: "#0d9488",
    Icon: Icons.sun,
    match: (t) => norm(t).includes("photovolt"),
    placeholder: true,
  },
  {
    key: "sonstiges",
    label: "Sonstiges",
    color: "#475569",
    Icon: Icons.list,
    match: (t) => norm(t).includes("sonst"),
    placeholder: true,
  },
];

/** Maps a Risikoobjekt-Tile key to the form `riskType` opened in /risks/new. */
const RISIKOOBJEKT_FORM_TYPE: Record<string, NewRiskType | null> = {
  kfz: "kfz",
  maschinen: "machine",
  selbstfahrend: "machine",
  immo: "immobilie",
  umwelt: "umwelt",
  photovoltaik: "photovoltaik",
  sonstiges: "sonstiges",
};

/* ──────────────────────────────────────────────────────────────────────────
   Styles
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Farbwerte der Abschnitte als CSS-Variablen statt fester Tokens.
 *
 * WARUM: Der Reiter läuft normalerweise in Aubergine/Gold. Steht der ganze
 * Unternehmensverbund in der Immobilienwirtschaft, trägt er die blaue Skala
 * (`immoTokens`). `makeStyles` erzeugt seine Klassen aber EINMAL beim Laden des
 * Moduls — eine zweite Farbfassung hieße, jeden dieser Schlüssel ein zweites
 * Mal zu pflegen. Über Variablen mit Rückfallwert genügt es, sie am Wrapper zu
 * überschreiben; ohne Überschreibung bleibt alles exakt wie bisher.
 */
const V = {
  ink: "var(--risk-ink, #2d142b)",
  akzent: "var(--risk-akzent, #4d2051)",
  gold: "var(--risk-gold, #f7a823)",
  muted: "var(--risk-muted, #7c7480)",
  linie: "var(--risk-linie, #ECE7F0)",
  /** Fläche des Funktions-Chips („Betriebsgesellschaft"). */
  chipBg: "var(--risk-chip-bg, rgba(247,168,35,0.16))",
  /** Fläche des ausgewählten Firmen-Chips. */
  chipOn: "var(--risk-chip-on, rgba(45,20,43,0.06))",
  /** Verlauf der Trennlinie rechts neben der Abschnittsüberschrift. */
  rule: "var(--risk-rule, linear-gradient(90deg, #f7a823 0%, rgba(247,168,35,0) 100%))",
} as const;

const useStyles = makeStyles({
  root: {
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    paddingTop: "12px",
    paddingBottom: "32px",
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },

  /* Top sticky toolbar */
  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "12px",
    rowGap: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 2px 6px rgba(45,20,43,0.04)",
  },
  toolbarSpacer: { flexGrow: 1 },
  toolbarTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.95rem",
    fontWeight: 600,
    color: BRAND.ink,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  toolbarIcon: {
    width: "18px",
    height: "18px",
    color: BRAND.gold,
    display: "inline-flex",
  },
  badgeButton: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: BRAND.ink,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    position: "relative",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      color: BRAND.purple,
    },
  },
  badgeIcon: { color: BRAND.purple, display: "inline-flex" },
  badgeSup: {
    position: "absolute",
    top: "-6px",
    right: "-6px",
    minWidth: "18px",
    height: "18px",
    paddingLeft: "4px",
    paddingRight: "4px",
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    fontSize: "0.7rem",
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    ...shorthands.borderRadius("999px"),
  },
  badgeSupRed: { backgroundColor: "#d54545", color: BRAND.white },

  /* Section frame */
  section: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  /** Inhaltshülle des Abschnitts — hält den Zeilenabstand der Sektion. */
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  sectionBodyZu: { display: "none" },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingBottom: "8px",
    ...shorthands.borderBottom("2px", "solid", BRAND.ink),
  },
  sectionHeadBtn: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    width: "100%",
    paddingTop: "0",
    paddingBottom: "8px",
    paddingLeft: "0",
    paddingRight: "0",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderBottom("2px", "solid", V.ink),
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: BRAND.fontPrimary,
  },
  /** Kopf mit Knopf rechts: die Linie sitzt unter der ganzen Zeile. */
  sectionHeadZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingBottom: "8px",
    ...shorthands.borderBottom("2px", "solid", V.ink),
  },
  sectionHeadBtnMitAktion: {
    flexGrow: 1,
    minWidth: 0,
    paddingBottom: "0",
    ...shorthands.borderBottom("0"),
  },
  sectionChevron: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    display: "inline-flex",
    color: V.ink,
    transitionProperty: "transform",
    transitionDuration: "150ms",
  },
  sectionChevronOffen: {
    transform: "rotate(90deg)",
  },
  sectionMeta: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: V.muted,
    flexShrink: 0,
  },
  sectionRule: {
    flexGrow: 1,
    height: "3px",
    backgroundImage: V.rule,
    ...shorthands.borderRadius("2px"),
  },
  sectionLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    fontWeight: 700,
    color: V.ink,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "10px",
    paddingLeft: "10px",
    ...shorthands.borderLeft("4px", "solid", V.gold),
  },
  sectionLabelIcon: {
    width: "22px",
    height: "22px",
    color: V.gold,
    display: "inline-flex",
  },

  betriebRowPlaceholder: {
    opacity: 0.65,
  },

  /* ── Firmen-Scope (Ebene + Firmenauswahl) über Betriebsrisiko/Matrix ── */
  scopeBar: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "14px",
  },
  scopeRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  scopeLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: V.muted,
    minWidth: "56px",
  },
  seg: {
    display: "inline-flex",
    ...shorthands.border("1.5px", "solid", V.linie),
    ...shorthands.borderRadius("999px"),
    overflow: "hidden",
    backgroundColor: BRAND.white,
  },
  segBtn: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: 700,
    color: V.ink,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "14px",
    paddingRight: "14px",
  },
  segBtnOn: {
    backgroundColor: V.akzent,
    color: BRAND.white,
  },
  scopeHint: {
    fontSize: "0.75rem",
    color: V.muted,
  },
  /** Beziehung zur Ausgangsfirma als Zusatz am Firmen-Chip („· Tochterfirma"). */
  firmChipRole: {
    fontWeight: 400,
    color: V.muted,
  },
  firmChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.border("1px", "solid", V.linie),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.white,
    cursor: "pointer",
    fontSize: "0.78rem",
    color: V.ink,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  firmChipOn: {
    ...shorthands.borderColor(V.akzent),
    backgroundColor: V.chipOn,
    fontWeight: 700,
  },

  /* ── Ein Unternehmen, zentriert (Abschnitt „Branche und Tätigkeit") ──
     Größer und dunkler als die übrigen Listen des Reiters: hier steht, worauf
     jede weitere Aussage über Risiken aufbaut. */
  unBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    rowGap: "4px",
    paddingTop: "10px",
    paddingBottom: "6px",
  },
  unName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.32rem",
    fontWeight: 800,
    color: V.ink,
    lineHeight: 1.2,
  },
  unAnschrift: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: V.muted,
  },
  unFeld: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "2px",
    marginTop: "14px",
    maxWidth: "640px",
  },
  unLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: V.muted,
  },
  unWert: {
    fontSize: "1.02rem",
    fontWeight: 600,
    color: V.ink,
    lineHeight: 1.4,
    // Die Tätigkeitsbeschreibung ist seit dem 27.08.2026 gegliederter Freitext
    // (Zeilen und Aufzählungspunkte, siehe utils/aufzaehlungText). Ohne
    // `pre-wrap` fiele die ganze Gliederung hier zu einer Zeile zusammen —
    // gerade in der Ansicht, die zeigen soll, worauf die Risikobetrachtung fußt.
    whiteSpace: "pre-wrap",
  },
  unLeer: {
    fontSize: "0.95rem",
    color: V.muted,
    fontStyle: "italic",
  },
  unStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
  },
  unBranche: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "1.02rem",
    fontWeight: 600,
    color: V.ink,
  },
  unBrancheIco: {
    width: "18px",
    height: "18px",
    flexShrink: 0,
    color: V.gold,
    display: "inline-flex",
  },
  unChips: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "6px",
  },
  unChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "11px",
    paddingRight: "13px",
    ...shorthands.borderRadius("999px"),
    backgroundColor: V.chipBg,
    color: V.ink,
    fontSize: "0.9rem",
    fontWeight: 700,
  },
  unChipIco: {
    width: "15px",
    height: "15px",
    flexShrink: 0,
    display: "inline-flex",
  },
  /* Das Organigramm sitzt direkt im Abschnitt — ohne Karte, ohne Knopf. */
  orgInner: {
    marginTop: "12px",
  },

  /* Section B boxes */
  boxCard: {
    backgroundColor: BRAND.white,
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("4px", "solid", BRAND.gold),
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 6px 14px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  boxHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  boxIcon: {
    width: "32px",
    height: "32px",
    color: BRAND.gold,
    display: "inline-flex",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "6px",
    paddingRight: "6px",
    boxSizing: "border-box",
    backgroundColor: "rgba(247,168,35,0.12)",
    ...shorthands.borderRadius("8px"),
  },
  boxTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    fontWeight: 700,
    color: BRAND.ink,
    flexGrow: 1,
  },
  boxSub: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontWeight: 600,
  },
  boxGrid: {
    display: "grid",
    gap: "8px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 540px)": { gridTemplateColumns: "1fr 1fr" },
  },

  /* Box row for Betrieb-Risiken (boolean) */
  betriebRow: {
    cursor: "pointer",
    appearance: "none",
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "160ms",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
    },
  },
  betriebRowOn: {
    backgroundColor: "rgba(247,168,35,0.06)",
    ...shorthands.borderColor(BRAND.gold),
  },
  betriebDot: {
    width: "34px",
    height: "34px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "7px",
    paddingRight: "7px",
    boxSizing: "border-box",
    color: BRAND.white,
    ...shorthands.borderRadius("8px"),
  },
  betriebText: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  betriebLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.95rem",
    fontWeight: 600,
    color: BRAND.ink,
    lineHeight: 1.1,
  },
  betriebStatus: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginTop: "2px",
  },
  betriebStatusOn: { color: "#15803d" },
  betriebStatusIcon: {
    width: "22px",
    height: "22px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.muted,
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("999px"),
  },
  betriebStatusIconOn: {
    color: BRAND.white,
    backgroundColor: "#15803d",
  },

  /* Box row for Risikoobjekte (count) */
  objektRow: {
    cursor: "pointer",
    appearance: "none",
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "160ms",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
    },
  },
  objektRowOn: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "rgba(77,32,81,0.04)",
  },
  objektCount: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.5rem",
    fontWeight: 700,
    color: BRAND.ink,
    minWidth: "32px",
    textAlign: "right",
    lineHeight: 1,
  },
  objektCountEmpty: {
    color: BRAND.muted,
  },

  /* Expanded panel below section B */
  expandPanel: {
    backgroundColor: BRAND.white,
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("4px", "solid", BRAND.purple),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 6px 14px rgba(45,20,43,0.06)",
  },
  expandHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    marginBottom: "12px",
  },
  expandTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    fontWeight: 600,
    flexGrow: 1,
  },
  expandClose: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    color: BRAND.muted,
    ...shorthands.border("0"),
    ":hover": { color: BRAND.ink },
  },

  /* Risk row table */
  rowsTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: BRAND.fontPrimary,
  },
  th: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: BRAND.muted,
    textAlign: "left",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  td: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.92rem",
    color: BRAND.ink,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    verticalAlign: "middle",
  },
  vorgangBadgeInline: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "5px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "7px",
    paddingRight: "9px",
    fontSize: "0.78rem",
    fontWeight: 700,
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    ...shorthands.borderRadius("999px"),
  },
  vorgangBadgeInlineEmpty: {
    backgroundColor: BRAND.bg,
    color: BRAND.muted,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  detailLink: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    ...shorthands.border("0"),
    ":hover": { textDecorationLine: "underline" },
  },
  contractLink: {
    cursor: "pointer",
    color: BRAND.purple,
    backgroundColor: "transparent",
    fontFamily: BRAND.fontPrimary,
    fontSize: "inherit",
    appearance: "none",
    textDecorationLine: "underline",
    paddingLeft: 0,
    paddingRight: 0,
    ...shorthands.border("0"),
  },
  emptyRow: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "10px",
    paddingRight: "10px",
    color: BRAND.muted,
    fontSize: "0.88rem",
    fontStyle: "italic",
    textAlign: "center",
  },

  /* Buttons */
  primaryBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    width: "fit-content",
    ":hover": { backgroundColor: BRAND.ink, ...shorthands.borderColor(BRAND.ink) },
    ":disabled": { cursor: "not-allowed", opacity: 0.5 },
  },
  goldBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.gold,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.gold),
    ...shorthands.borderRadius("8px"),
    width: "fit-content",
    ":hover": { backgroundColor: "#e89d20", ...shorthands.borderColor("#e89d20") },
  },
  ghostBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 600,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    width: "fit-content",
    ":hover": { backgroundColor: BRAND.bg },
  },

  /* Drawer / overlay */
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(45,20,43,0.55)",
    zIndex: 9999,
    display: "flex",
    justifyContent: "flex-end",
  },
  drawer: {
    width: "min(520px, 96vw)",
    backgroundColor: BRAND.white,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "-12px 0 32px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  drawerHead: {
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  drawerTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    flexGrow: 1,
    color: BRAND.gold,
  },
  drawerSub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.8)",
  },
  drawerBody: {
    flexGrow: 1,
    overflowY: "auto",
    paddingTop: "16px",
    paddingBottom: "24px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    backgroundColor: BRAND.bg,
  },
  drawerCard: {
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  drawerVorgang: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    marginBottom: "8px",
  },
  drawerVorgangTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    fontWeight: 600,
    flexGrow: 1,
  },
  drawerVorgangMeta: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  drawerTodo: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "8px",
    paddingBottom: "8px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  drawerTodoFirst: { ...shorthands.borderTop("0") },
  todoStatus: {
    width: "10px",
    height: "10px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  todoTitle: {
    fontSize: "0.9rem",
    color: BRAND.ink,
    fontWeight: 600,
    flexGrow: 1,
  },
  todoMeta: {
    fontSize: "0.74rem",
    color: BRAND.muted,
  },
  emptyDrawer: {
    padding: "24px",
    textAlign: "center",
    color: BRAND.muted,
    fontSize: "0.9rem",
  },

  /* Empty hero */
  emptyHero: {
    backgroundColor: BRAND.white,
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "32px",
    paddingRight: "32px",
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    alignItems: "center",
  },
  emptyTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.5rem",
    color: BRAND.ink,
    margin: 0,
  },
  emptyDesc: {
    color: BRAND.muted,
    fontSize: "0.95rem",
    maxWidth: "560px",
    marginTop: 0,
    marginBottom: "12px",
  },

  /* Auswertung modal */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(20,8,20,0.55)",
    backdropFilter: "blur(2px)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  modalPanel: {
    width: "min(720px, 100%)",
    maxHeight: "min(720px, 92vh)",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 24px 60px rgba(20,8,20,0.32)",
  },
  modalHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "14px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  modalHeadIcon: {
    width: "32px",
    height: "32px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "6px",
    paddingRight: "6px",
    boxSizing: "border-box",
    color: BRAND.white,
    ...shorthands.borderRadius("8px"),
  },
  modalTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.15rem",
    fontWeight: 600,
    color: BRAND.ink,
    flexGrow: 1,
    margin: 0,
  },
  modalSub: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontWeight: 600,
  },
  modalBody: {
    flexGrow: 1,
    overflowY: "auto",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
    backgroundColor: BRAND.bg,
  },
  modalFoot: {
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "10px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "18px",
    paddingRight: "18px",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
  },
  auswertungRiskCard: {
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  auswertungRiskName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.ink,
    marginBottom: "8px",
  },
  auswertungCharRow: {
    display: "grid",
    gridTemplateColumns: "minmax(140px, 35%) 1fr",
    columnGap: "12px",
    rowGap: "4px",
    paddingTop: "6px",
    paddingBottom: "6px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
    fontSize: "0.88rem",
  },
  auswertungCharRowFirst: { ...shorthands.borderTop("0") },
  auswertungCharKey: {
    color: BRAND.muted,
    fontWeight: 600,
  },
  auswertungCharVal: {
    color: BRAND.ink,
    wordBreak: "break-word",
  },
  auswertungEmpty: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    color: BRAND.muted,
    fontSize: "0.9rem",
    textAlign: "center",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
});

/* ──────────────────────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────────────────────── */

const fmtDate = (iso?: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE");
  } catch {
    return iso;
  }
};

const isOpenStatus = (s: string | undefined) =>
  !!s && s.toLowerCase() !== "erledigt";

const isOverdueOrDue = (iso?: string | null): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return d.getTime() <= today.getTime();
};

/**
 * Ein zuklappbarer Abschnitt des Reiters.
 *
 * Startzustand hängt am Inhalt: was erfasst ist, steht offen da; was leer ist,
 * startet zugeklappt und nimmt nur eine Zeile ein. Der Reiter ist fünf
 * Abschnitte lang, und bei einem frisch angelegten Kunden sind die meisten
 * davon leer — ausgeklappt wären das fünf Bildschirme „nichts erfasst".
 *
 * `gefuellt` steuert NUR den Startwert. Sobald jemand selbst geklappt hat,
 * gewinnt seine Wahl — auch wenn danach der erste Eintrag entsteht.
 */
const Abschnitt: React.FC<{
  titel: string;
  icon: React.ReactNode;
  gefuellt: boolean;
  /**
   * Startet zugeklappt, auch wenn Inhalt da ist — für Abschnitte, die man
   * gezielt aufschlägt statt sie beim Scrollen zu passieren.
   */
  startZu?: boolean;
  /** Kurzinfo rechts im Kopf, z. B. „3 Firmen" oder „leer". */
  meta?: string;
  /** Knopf ganz rechts im Kopf (z. B. „+") — neben, nicht im Klapp-Knopf. */
  aktion?: React.ReactNode;
  children: React.ReactNode;
}> = ({ titel, icon, gefuellt, startZu = false, meta, aktion, children }) => {
  const styles = useStyles();
  const [offen, setOffen] = useState(gefuellt && !startZu);
  // Der Inhalt kommt nachgeladen: beim ersten Rendern ist noch alles leer, und
  // ein Abschnitt, der erst danach Inhalt bekommt, soll trotzdem offen stehen.
  // Deshalb bis zum ersten Klick dem Inhalt folgen.
  const [selbstGeklappt, setSelbstGeklappt] = useState(false);
  useEffect(() => {
    if (!selbstGeklappt) setOffen(gefuellt && !startZu);
  }, [gefuellt, selbstGeklappt, startZu]);

  const kopf = (
    <button
      type="button"
      className={mergeClasses(
        styles.sectionHeadBtn,
        aktion != null && styles.sectionHeadBtnMitAktion,
      )}
      aria-expanded={offen}
      onClick={() => {
        setSelbstGeklappt(true);
        setOffen((v) => !v);
      }}
    >
      <span
        className={mergeClasses(styles.sectionChevron, offen && styles.sectionChevronOffen)}
        aria-hidden
      >
        <Icons.chevronRight />
      </span>
      <span className={styles.sectionLabel}>
        <span className={styles.sectionLabelIcon}>{icon}</span>
        {titel}
      </span>
      <span className={styles.sectionRule} />
      {meta && <span className={styles.sectionMeta}>{meta}</span>}
    </button>
  );

  return (
    <div className={styles.section}>
      {aktion != null ? (
        <div className={styles.sectionHeadZeile}>
          {kopf}
          {aktion}
        </div>
      ) : (
        kopf
      )}
      {/* Der Inhalt bleibt MONTIERT und wird nur ausgeblendet. Die drei
          nachgeladenen Abschnitte melden ihren Füllstand über `onBefund` —
          hingen sie am Aufklapp-Zustand, könnten sie nie melden, dass sie
          gefüllt sind, und blieben dauerhaft zugeklappt. */}
      <div className={offen ? styles.sectionBody : styles.sectionBodyZu} aria-hidden={!offen}>
        {children}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────────────── */

type Props = {
  contactId: number;
  onSelectTab?: (tabValue: string) => void;
  contactTyp?: string;
  /** Anzeigename des Kontakts — für Window-Titel beim Anlegen von Risiken. */
  contactName?: string | null;
};

const ContactRisksPanel = ({
  contactId: propContactId,
  contactTyp,
  contactName: propContactName,
}: Props) => {
  const styles = useStyles();
  const navigate = useNavigate();

  const contactId = propContactId;
  const contactName = propContactName;

  // ContactDetails liefert "Person" oder "Firma" (siehe ContactDetails.tsx Z.2101).
  // Fallback: alles was nicht explizit "Firma" ist, behandeln wir als private Person.
  const isPrivat = !!contactTyp && contactTyp !== "Firma";

  const [risks, setRisks] = useState<FullRiskObject[]>([]);
  const [vorgaenge, setVorgaenge] = useState<VorgangDto[]>([]);
  const [expandedObjekt, setExpandedObjekt] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<null | "vorgaenge" | "twv">(null);
  /**
   * Füllstand der drei nachgeladenen Abschnitte — sie entscheiden, ob der
   * jeweilige Abschnitt offen oder zugeklappt startet. Die Zahlen kennen nur
   * die Kind-Komponenten (sie laden ihre Daten selbst), deshalb melden sie sie
   * über `onBefund` nach oben statt hier ein zweites Mal geladen zu werden.
   */
  const [matrixAnzahl, setMatrixAnzahl] = useState(0);
  const [importOffen, setImportOffen] = useState(false);
  /**
   * Nach einem Import muss die Matrix ihre Positionen neu holen. Ein
   * Schlüsselwechsel hängt sie frisch ein — sie lädt selbst und kennt keinen
   * Weg, von außen zum Nachladen aufgefordert zu werden.
   */
  const [matrixKey, setMatrixKey] = useState(0);
  const [ertragErfasst, setErtragErfasst] = useState(false);
  const [immoAnzahl, setImmoAnzahl] = useState(0);
  /**
   * Beauftragte Hausverwaltungen — sie stehen an den Gebäuden, werden aber
   * ZWEI Abschnitte weiter oben gebraucht (Organigramm). Deshalb meldet die
   * Immobilienmappe sie hierher; ein zweiter Ladepfad für dieselben Objekte
   * wäre derselbe Request ein zweites Mal.
   */
  const [verwaltungen, setVerwaltungen] = useState<Verwaltung[]>([]);

  /* ─────── Firmen-Scope (SP2) ───────
     Ebene + Firmenauswahl liegen hier statt in der Vermögens-Matrix, weil sie
     seit dem Umbau mehrere Abschnitte gemeinsam steuern (Betriebsrisiko-Karte,
     Matrix, später Objektmappen + Ertragsausfall). Die Matrix bekommt Scope und
     Auswahl als Props. Nur Firmen — bei Privatkontakten bleibt alles ungeladen. */
  const network = useCompanyNetwork(isPrivat ? null : contactId);
  // Branchenkatalog ist modulweit gecacht — der Hook löst nur beim ersten
  // Verbraucher pro Session einen Request aus.
  const { rows: brancheRows, loading: brancheLoading } = useBrancheClassification();
  const [ebene, setEbene] = useState<Ebene>("firma");
  const [selectedFirmen, setSelectedFirmen] = useState<Set<number>>(
    () => new Set([propContactId]),
  );

  /**
   * Die Ausgangsfirma kommt aus derselben Antwort wie der Rest des Verbunds —
   * sie wird hier NICHT neu zusammengesetzt, sondern übernommen. Vorher trug
   * dieses Objekt nur Branche und Besonderheiten, weshalb auf der Ebene
   * „Dieses Unternehmen" Funktion, Tätigkeit und Anschrift fehlten, obwohl
   * sie gepflegt waren.
   */
  const selbstFirma = useMemo<CompanyNetworkFirma>(() => {
    const ausNetz = network.find((f) => f.id === contactId);
    return {
      ...ausNetz,
      id: contactId,
      name: contactName ?? ausNetz?.name ?? `Kontakt ${contactId}`,
    };
  }, [network, contactId, contactName]);

  /**
   * Der ganze Verbund — unabhängig von der gewählten Ebene. Die Ebene steuert,
   * welche Firmen die Abschnitte AUSWERTEN; ob der Reiter ein Immobilienreiter
   * ist, entscheidet dagegen der Verbund als Ganzes. Sonst wechselte die
   * Gliederung beim Umschalten der Ebene.
   */
  const verbundFirmen = useMemo<CompanyNetworkFirma[]>(() => {
    const map = new Map<number, CompanyNetworkFirma>();
    map.set(selbstFirma.id, selbstFirma);
    for (const f of network) if (!map.has(f.id)) map.set(f.id, f);
    return Array.from(map.values());
  }, [selbstFirma, network]);

  const scopeFirmen = useMemo<CompanyNetworkFirma[]>(
    () => (ebene === "firma" ? [selbstFirma] : verbundFirmen),
    [ebene, selbstFirma, verbundFirmen],
  );

  // Ebenen-Wechsel oder neue Firmenliste → alle Firmen der Ebene auswählen
  const scopeKey = useMemo(
    () => `${ebene}|${scopeFirmen.map((f) => f.id).join(",")}`,
    [ebene, scopeFirmen],
  );
  useEffect(() => {
    setSelectedFirmen(new Set(scopeFirmen.map((f) => f.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const selectedFirmenIds = useMemo(
    () =>
      scopeFirmen
        .map((f) => f.id)
        .filter((id) => selectedFirmen.has(id))
        .sort((a, b) => a - b),
    [scopeFirmen, selectedFirmen],
  );

  /**
   * Organigramm statt Einzelblock. Beides hängt an der Ebene — mit einer
   * Einschränkung: eine Firma OHNE Verbindungen ergibt auf der Ebene
   * „Unternehmensverbund" ein Organigramm aus einem einzigen Kästchen. Das
   * sagt weniger als der Block und sähe nach einem Fehler aus.
   */
  const verbundZeigen = ebene !== "firma" && scopeFirmen.length > 1;

  /**
   * Immobilienreiter statt Betriebsreiter: Steht der ganze Verbund in der
   * Immobilienwirtschaft, treten Vermögensmatrix, Objektmappen und
   * Ertragsausfall zurück — es zählt das einzelne Gebäude. Die Regel selbst
   * steht in `immobilienVerbund.ts`.
   */
  const immoModus = useMemo(
    () => istImmobilienVerbund(brancheRows, verbundFirmen, contactId),
    [brancheRows, verbundFirmen, contactId],
  );

  const toggleFirm = useCallback(
    (id: number) =>
      setSelectedFirmen((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        // Leere Auswahl wäre eine tote Ansicht → immer mindestens der Kontakt selbst.
        if (next.size === 0) next.add(contactId);
        return next;
      }),
    [contactId],
  );

  const { openWindow } = useWindowManager();
  /** Objektmappen: einmal geladen, auf die Abschnitte nach Objektart verteilt. */
  const mappen = useObjektmappen(isPrivat || immoModus ? [] : selectedFirmenIds);
  const mappenBefund = objektmappenBefund(mappen);

  const goToRiskForm = useCallback(
    (riskType: NewRiskType | null) => {
      openRiskFormWindow(openWindow, {
        riskType: riskType ?? undefined,
        contactId: contactId ?? null,
        contactName: contactName ?? null,
        initialSize: { width: 1080, height: 720 },
      });
    },
    [contactId, contactName, openWindow]
  );

  const refresh = useCallback(async () => {
    try {
      // Die Analyse-Quellen (public-forms, risikoanalyse/internal-state) lädt
      // der Analyse-Knopf im Reiter „Allgemein" selbst (useAnalysenState) —
      // hier bleiben nur die Quellen der Abschnitte dieses Reiters.
      const [risksRes, allVorgaenge] = await Promise.all([
        fetch(apiUrl(`risk-objects/by-contact/${contactId}`)),
        vorgangService.getAllVorgaenge().catch(() => [] as VorgangDto[]),
      ]);
      if (risksRes.ok) {
        const data = await risksRes.json();
        setRisks(Array.isArray(data) ? (data as FullRiskObject[]) : []);
      }
      setVorgaenge(allVorgaenge);
    } catch {
      /* ignore */
    }
  }, [contactId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const realRisks = useMemo(
    () =>
      risks.filter(
        (r) =>
          !(typeof r.name === "string" && r.name.includes("wartet auf Detailerfassung"))
      ),
    [risks]
  );

  /* Vorgaenge / TWV summaries */
  const riskIdSet = useMemo(() => new Set(realRisks.map((r) => r.id)), [realRisks]);
  const riskAssignedVorgaenge = useMemo(() => {
    return vorgaenge
      .map((v) => {
        const todos = (v.todos || []).filter((t) =>
          (t.assignedRisks || []).some((ar) => riskIdSet.has(ar.id))
        );
        return { ...v, todos };
      })
      .filter((v) => v.todos.length > 0);
  }, [vorgaenge, riskIdSet]);
  const openTodosByRiskId = useMemo(() => {
    const m = new Map<number, number>();
    for (const v of riskAssignedVorgaenge) {
      for (const t of v.todos) {
        if (!isOpenStatus(t.status)) continue;
        for (const ar of t.assignedRisks || []) {
          if (!riskIdSet.has(ar.id)) continue;
          m.set(ar.id, (m.get(ar.id) ?? 0) + 1);
        }
      }
    }
    return m;
  }, [riskAssignedVorgaenge, riskIdSet]);
  const offeneVorgaengeCount = useMemo(
    () =>
      riskAssignedVorgaenge.filter((v) =>
        v.todos.some((t) => isOpenStatus(t.status))
      ).length,
    [riskAssignedVorgaenge]
  );
  const twvCount = useMemo(() => {
    let n = 0;
    for (const v of riskAssignedVorgaenge) {
      for (const t of v.todos) {
        if (!isOpenStatus(t.status)) continue;
        if (isOverdueOrDue(t.twv)) n++;
      }
    }
    return n;
  }, [riskAssignedVorgaenge]);

  /* ─────── Render helpers ─────── */

  const objektRowsFor = (tile: RisikoobjektTile): FullRiskObject[] => {
    return realRisks.filter((r) => tile.match(r.riskType ?? ""));
  };

  /* ─────── Render: Firmen-Scope (Ebene + Firmenauswahl) ─────── */

  const renderScopeBar = () => (
    <div className={styles.scopeBar}>
      <div className={styles.scopeRow}>
        <span className={styles.scopeLabel}>Ebene</span>
        <div className={styles.seg}>
          {EBENEN.map((e) => (
            <button
              key={e.id}
              type="button"
              className={mergeClasses(styles.segBtn, ebene === e.id && styles.segBtnOn)}
              onClick={() => setEbene(e.id)}
            >
              {e.label}
            </button>
          ))}
        </div>
        <span className={styles.scopeHint}>{EBENEN.find((e) => e.id === ebene)?.hint}</span>
      </div>
      {ebene !== "firma" && scopeFirmen.length > 1 && (
        <div className={styles.scopeRow}>
          <span className={styles.scopeLabel}>Firmen</span>
          {scopeFirmen.map((f) => (
            <button
              key={f.id}
              type="button"
              className={mergeClasses(
                styles.firmChip,
                selectedFirmen.has(f.id) && styles.firmChipOn,
              )}
              onClick={() => toggleFirm(f.id)}
            >
              {f.name ?? `Kontakt ${f.id}`}
              {f.role ? <span className={styles.firmChipRole}> · {f.role}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  /* ─────── Render: Branche und Tätigkeit ─────── */

  /**
   * Ein Unternehmen, zentriert: Name mit Anschrift, darunter Funktion, Branche
   * und die beiden Tätigkeitsangaben — jeweils Bezeichnung über dem Wert.
   *
   * KEINE Karte drumherum und keine eigene Überschrift: der Abschnitt heißt
   * bereits „Branche und Tätigkeit", und eine Karte mit demselben Titel darin
   * wäre ein Rahmen um einen Rahmen. Die Angaben stehen größer und dunkler als
   * die übrigen Listen des Reiters, weil auf ihnen alles Weitere aufbaut.
   *
   * Fehlt ein Wert, steht „nicht erfasst" da statt der Zeile zu entfallen: wer
   * prüft, worauf die Risikobetrachtung fußt, muss die Lücke sehen.
   */
  const renderDiesesUnternehmen = (f: CompanyNetworkFirma) => {
    const funktionen = funktionenAus(f.unternehmensfunktion);
    const branchen = branchenAus(brancheRows, f.brancheClassId, f.weitereBranchen);
    const taetigkeit = (f.taetigkeit ?? f.besonderheiten ?? "").trim();
    const lautKunde = (f.taetigkeitLautKunde ?? "").trim();
    const anschrift = (f.anschrift ?? "").trim();

    const feld = (bezeichnung: string, inhalt: React.ReactNode, gefuellt: boolean) => (
      <div className={styles.unFeld}>
        <span className={styles.unLabel}>{bezeichnung}</span>
        <span className={gefuellt ? styles.unWert : styles.unLeer}>
          {gefuellt ? inhalt : brancheLoading ? "lädt …" : "nicht erfasst"}
        </span>
      </div>
    );

    return (
      <div className={styles.unBlock}>
        <span className={styles.unName}>{f.name ?? `Kontakt ${f.id}`}</span>
        <span className={anschrift ? styles.unAnschrift : styles.unLeer}>
          {anschrift || "Anschrift nicht erfasst"}
        </span>

        {feld(
          "Unternehmensfunktion",
          <span className={styles.unChips}>
            {funktionen.map((fn) => (
              <span key={fn} className={styles.unChip}>
                <span className={styles.unChipIco}>
                  <IconRahmen>{unternehmensfunktionIcon(fn)}</IconRahmen>
                </span>
                {fn}
              </span>
            ))}
          </span>,
          funktionen.length > 0,
        )}

        {feld(
          branchen.length > 1 ? "Branchen" : "Branche",
          <span className={styles.unStack}>
            {branchen.map((b) => (
              <span key={b.id} className={styles.unBranche}>
                <span className={styles.unBrancheIco}>
                  <IconRahmen titel={b.ebene1}>{brancheIcon(b.ebene1)}</IconRahmen>
                </span>
                {b.pfad}
              </span>
            ))}
          </span>,
          branchen.length > 0,
        )}

        {/* „Tätigkeit / Betriebscharakter" führt beide Begriffe, weil der
            Fragebogen den Kunden genau so danach fragt — die Antwort wäre unter
            nur einem der beiden Namen nicht wiederzuerkennen. */}
        {feld("Tätigkeit / Betriebscharakter", taetigkeit, !!taetigkeit)}
        {feld("Tätigkeitsbeschreibung laut Kunde", lautKunde, !!lautKunde)}
      </div>
    );
  };

  /**
   * Der Unternehmensverbund als Organigramm — hierarchisch, nur Firmen, die
   * Mutter- bzw. Holdinggesellschaft oben. Es steht offen da statt hinter einem
   * Knopf: sobald mehr als eine Firma im Spiel ist, IST der Verbund die
   * Antwort auf „worauf beruht die Risikobetrachtung".
   */
  const renderVerbundOrganigramm = () => (
    <div className={styles.orgInner}>
      <OrganigrammPopup
        companyId={contactId}
        companyName={contactName ?? undefined}
        variant="inline"
        verbundAnsicht
        /* Im Immobilienverbund gehört die Hausverwaltung ins Bild: Dort werden
           die Objekte betreut, und sie muss keine Verbundfirma sein. */
        verwaltungen={immoModus ? verwaltungen : undefined}
      />
    </div>
  );

  /* ─────── Render: Inline expanded list (nur noch Privat-Zweig) ─────── */

  const renderExpandedObjekt = () => {
    if (!expandedObjekt) return null;
    const tile = RISIKOOBJEKTE.find((t) => t.key === expandedObjekt);
    if (!tile) return null;
    const rows = objektRowsFor(tile);
    return (
      <div className={styles.expandPanel}>
        <div className={styles.expandHead}>
          <span
            style={{
              width: 30,
              height: 30,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: tile.color,
              color: BRAND.white,
              borderRadius: 8,
              padding: 6,
              boxSizing: "border-box",
            }}
          >
            <tile.Icon />
          </span>
          <span className={styles.expandTitle}>{tile.label}</span>
          <button
            type="button"
            className={styles.expandClose}
            onClick={() => setExpandedObjekt(null)}
            aria-label="Schließen"
          >
            <span style={{ width: 18, height: 18, display: "inline-flex" }}>
              <Icons.close />
            </span>
          </button>
        </div>
        {rows.length === 0 ? (
          <div className={styles.emptyRow}>
            Noch keine {tile.label} erfasst.
          </div>
        ) : (
          <table className={styles.rowsTable}>
            <thead>
              <tr>
                <th className={styles.th}>Risikoobjekt</th>
                <th className={styles.th}>Offene Vorgänge</th>
                <th className={styles.th}>Vertrag</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = openTodosByRiskId.get(r.id) ?? 0;
                return (
                  <tr key={r.id}>
                    <td className={styles.td} style={{ fontWeight: 600 }}>
                      {risikoobjektLabel(r)}
                    </td>
                    <td className={styles.td}>
                      <span
                        className={mergeClasses(
                          styles.vorgangBadgeInline,
                          open === 0 && styles.vorgangBadgeInlineEmpty
                        )}
                      >
                        <span style={{ width: 12, height: 12, display: "inline-flex" }}>
                          <Icons.inbox />
                        </span>
                        {open}
                      </span>
                    </td>
                    <td className={styles.td}>
                      {r.contractID ? (
                        <button
                          type="button"
                          className={styles.contractLink}
                          onClick={() => navigate(`/contracts/${r.contractID}`)}
                        >
                          {r.contract?.versicherungsnummer ?? `#${r.contractID}`}
                        </button>
                      ) : (
                        <span style={{ color: BRAND.muted }}>—</span>
                      )}
                    </td>
                    <td className={styles.td} style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className={styles.detailLink}
                        onClick={() => navigate(`/risks/${r.id}`)}
                      >
                        Details
                        <span style={{ width: 14, height: 14, display: "inline-flex" }}>
                          <Icons.arrow />
                        </span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: 12,
          }}
        >
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() => goToRiskForm(RISIKOOBJEKT_FORM_TYPE[tile.key] ?? null)}
          >
            <span style={{ width: 14, height: 14, display: "inline-flex" }}>
              <Icons.plus />
            </span>
            {tile.label} hinzufügen
          </button>
        </div>
      </div>
    );
  };

  /* ─────── Render: full ─────── */

  // Private contacts only deal with their own Immo- and KFZ-Risikoobjekte
  // (no Allgemeine Analyse, no Detailanalyse, kein Betriebsrisiko). Wir
  // benutzen dieselben RISIKOOBJEKTE-Tiles wie fuer Firmen, gefiltert
  // auf die zwei relevanten Keys — dadurch funktioniert die Expand-Logik
  // (renderExpandedObjekt + objektRowsFor) unveraendert.
  const renderPrivateRisikoobjekteBox = () => {
    const tiles = RISIKOOBJEKTE.filter(
      (t) => t.key === "immo" || t.key === "kfz",
    );
    return (
      <div className={styles.boxCard}>
        <div className={styles.boxHead}>
          <span className={styles.boxIcon}>
            <Icons.briefcase />
          </span>
          <span className={styles.boxTitle}>Risikoobjekte</span>
          <span className={styles.boxSub}>Immobilien &amp; KFZ</span>
        </div>
        <div className={styles.boxGrid}>
          {tiles.map((tile) => {
            const rows = objektRowsFor(tile);
            const count = rows.length;
            const active = expandedObjekt === tile.key;
            return (
              <button
                key={tile.key}
                type="button"
                className={mergeClasses(
                  styles.objektRow,
                  active && styles.objektRowOn,
                )}
                onClick={() =>
                  setExpandedObjekt((prev) =>
                    prev === tile.key ? null : tile.key,
                  )
                }
              >
                <span
                  className={styles.betriebDot}
                  style={{ backgroundColor: tile.color }}
                >
                  <tile.Icon />
                </span>
                <div className={styles.betriebText}>
                  <span className={styles.betriebLabel}>{tile.label}</span>
                  <span
                    className={mergeClasses(
                      styles.betriebStatus,
                      count > 0 && styles.betriebStatusOn,
                    )}
                  >
                    {count === 0
                      ? "keine erfasst"
                      : count === 1
                      ? "1 Objekt"
                      : `${count} Objekte`}
                  </span>
                </div>
                <span
                  className={mergeClasses(
                    styles.objektCount,
                    count === 0 && styles.objektCountEmpty,
                  )}
                >
                  {count > 0 ? count : "—"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Private-only header: zwei Quick-Add-Buttons (KFZ + Immobilie) ueber den
  // beiden grossen Boxen, damit der User direkt anlegen kann ohne erst auf
  // eine Box klicken zu muessen.
  const renderPrivatQuickAdd = () => (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        padding: "10px 0 14px",
      }}
    >
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => goToRiskForm("kfz")}
      >
        <span style={{ width: 14, height: 14, display: "inline-flex" }}>
          <Icons.plus />
        </span>
        KFZ hinzufügen
      </button>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => goToRiskForm("immobilie")}
      >
        <span style={{ width: 14, height: 14, display: "inline-flex" }}>
          <Icons.plus />
        </span>
        Immobilie hinzufügen
      </button>
    </div>
  );

  return (
    /* Die blaue Skala gilt genau so weit wie die Immobilienansicht: als
       Variablen am Wrapper, damit weder `makeStyles`-Klassen noch das
       Organigramm-CSS doppelt gepflegt werden müssen (siehe `V` oben). */
    <div className={styles.root} style={immoModus ? IMMO_CSS_VARS : undefined}>
      {isPrivat ? (
        <>
          {renderPrivatQuickAdd()}
          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionLabel}>
                <span className={styles.sectionLabelIcon}>
                  <Icons.factory />
                </span>
                Risikoobjekte
              </span>
              <span className={styles.sectionRule} />
            </div>
            {renderPrivateRisikoobjekteBox()}
            {renderExpandedObjekt()}
          </div>
        </>
      ) : (
        <>
          {/* Erster Abschnitt: worauf die Risikobetrachtung fußt. Die
              Firmenauswahl steht hier oben, weil sie ab hier ALLE folgenden
              Abschnitte steuert. Die Analyse-Kacheln, die früher davor standen,
              sind in den Reiter „Allgemein" gewandert — sie füllen Stammdaten,
              sie zeigen keine Risiken. */}
          <Abschnitt
            titel="Branche und Tätigkeit"
            icon={<Icons.branchActivity />}
            gefuellt
            /* Die Kurzinfo beschreibt, was der Abschnitt ZEIGT: das Organigramm
               zeigt den ganzen Verbund, nicht die Firmenauswahl darunter — die
               steuert Matrix und Objektmappen. */
            meta={verbundZeigen ? `${scopeFirmen.length} Firmen im Verbund` : undefined}
          >
            {renderScopeBar()}
            {/* Ein Unternehmen liest sich als Block; ein Verbund nur als Bild.
                Sobald mehrere Firmen im Scope sind, sagt die Struktur — wer
                hält wen — mehr als eine Liste derselben Angaben untereinander. */}
            {verbundZeigen
              ? renderVerbundOrganigramm()
              : scopeFirmen[0]
                ? renderDiesesUnternehmen(scopeFirmen[0])
                : null}
          </Abschnitt>

          {immoModus ? (
            /* Immobilienverbund: Statt Matrix, Objektmappen und Ertragsausfall
               steht hier EIN Abschnitt — die Gebäude. Vermögensmatrix und
               Ertragsausfall setzen einen Betrieb voraus (Anlagevermögen,
               Rohertrag aus Produktion); bei einer Grundbesitzgesellschaft
               wären beide dauerhaft leer und verdeckten das Wesentliche. */
            <Abschnitt
              titel="Immobilienmappen"
              icon={<Icons.house />}
              gefuellt={immoAnzahl > 0}
              meta={immoAnzahl > 0 ? `${immoAnzahl} Gebäude` : "leer"}
            >
              {renderScopeBar()}
              <ImmobilienmappenSection
                selectedIds={selectedFirmenIds}
                verbundAnsicht={verbundZeigen}
                onBefund={setImmoAnzahl}
                onVerwaltungen={setVerwaltungen}
              />
            </Abschnitt>
          ) : (
            <>
          <Abschnitt
            titel="Betriebseinrichtung, Waren und Vorräte"
            icon={<Icons.chart />}
            gefuellt={matrixAnzahl > 0}
            meta={matrixAnzahl > 0 ? `${matrixAnzahl} Positionen` : "leer"}
          >
            {/* Dieselbe Ebene/Firmenauswahl wie im Abschnitt darüber — bewusst
                doppelt: dazwischen liegen Organigramm und Branchenliste, sodass
                oben nicht mehr sichtbar ist, auf welchen Firmen die Zahlen
                beruhen. Beide Leisten steuern denselben State. */}
            {renderScopeBar()}
            {/* Der Einstieg sitzt dort, wo das Ergebnis erscheint: Wer die Matrix
                ansieht und merkt, dass Positionen fehlen, kann sie hier einspielen. */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => setImportOffen(true)}
              >
                Risikoermittlung (intern)
              </button>
            </div>
            <VermoegensMatrix
              key={matrixKey}
              scopeFirmen={scopeFirmen}
              selectedIds={selectedFirmenIds}
              onBefund={setMatrixAnzahl}
            />
            <AnlagenImportDialog
              open={importOffen}
              onClose={() => setImportOffen(false)}
              kontaktId={contactId}
              kontaktName={selbstFirma?.name ?? contactName ?? ""}
              onFertig={() => setMatrixKey((k) => k + 1)}
            />
          </Abschnitt>

          {/* Umlaufvermögen: kein eigener Abschnitt mehr. Waren und Vorräte sind
              eine Kennzahl der Matrix darüber; die früher hier erfassten
              Vorratswerte der Kennzahlen erscheinen dort als eigene Zeilen. */}

          {/* Statt EINES Abschnitts „Objektmappen" (bis 11.10.2026) je Objektart
              ein eigener Abschnitt mit eigenen Mappen. Gebäude, PV und Kfz
              sind nicht Teil der Betriebsvermögens-Summe darüber; Elektronik
              und Maschinen zeigen ihre Summe aus ihr. */}
          <Abschnitt
            titel="Technische Betriebseinrichtung (wenn nicht pauschal, sondern per Einzeldeklaration)"
            icon={<Icons.factory />}
            gefuellt={mappenBefund.technik > 0}
          >
            {/* Je Unterabschnitt ein eigenes „+" — EDV/Elektronik und Maschinen
                werden über verschiedene Masken angelegt. */}
            <ObjektmappenTeil teil="technik" daten={mappen} onNeu={goToRiskForm} />
          </Abschnitt>

          <Abschnitt
            titel="Betriebsgebäude und Grundstücke mit Photovoltaik"
            icon={<Icons.house />}
            gefuellt={mappenBefund.gebaeude > 0}
            aktion={
              <PlusKnopf
                label="Betriebsgebäude und Grundstücke"
                onClick={() => goToRiskForm("immobilie")}
              />
            }
          >
            <ObjektmappenTeil teil="gebaeude" daten={mappen} />
          </Abschnitt>

          <Abschnitt
            titel="KFZ"
            icon={<Icons.car />}
            gefuellt={mappenBefund.kfz > 0}
            aktion={<PlusKnopf label="KFZ" onClick={() => goToRiskForm("kfz")} />}
          >
            <ObjektmappenTeil teil="kfz" daten={mappen} />
          </Abschnitt>

          <Abschnitt
            titel="Umwelt"
            icon={<Icons.tree />}
            gefuellt={mappenBefund.umwelt > 0}
            aktion={<PlusKnopf label="Umwelt" onClick={() => goToRiskForm("umwelt")} />}
          >
            <ObjektmappenTeil teil="umwelt" daten={mappen} />
          </Abschnitt>

          {/* Ertragsausfall bezieht sich immer auf den geöffneten Kontakt, nicht
              auf die Firmenauswahl: Rohertrag und Haftzeit sind je Rechtsträger
              zu betrachten, eine Verbund-Summe wäre fachlich falsch. */}
          <Abschnitt
            titel="Ertragsausfall"
            icon={<Icons.lossChart />}
            gefuellt={ertragErfasst}
            meta={ertragErfasst ? undefined : "leer"}
          >
            <ErtragsausfallSection contactId={contactId} onBefund={setErtragErfasst} />
          </Abschnitt>
            </>
          )}
        </>
      )}

      {/* DRAWER */}
      {drawer && (
        <div
          className={styles.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setDrawer(null);
          }}
        >
          <aside className={styles.drawer}>
            <div className={styles.drawerHead}>
              <span style={{ width: 24, height: 24, display: "inline-flex", color: BRAND.gold }}>
                {drawer === "vorgaenge" ? <Icons.inbox /> : <Icons.clock />}
              </span>
              <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                <span className={styles.drawerTitle}>
                  {drawer === "vorgaenge" ? "Offene Vorgänge" : "Wiedervorlagen (TWV)"}
                </span>
                <span className={styles.drawerSub}>
                  {drawer === "vorgaenge"
                    ? "Vorgänge mit Todos, die einem Risiko zugeordnet sind."
                    : "Heute oder überfällig — nur Risiko-zugeordnete Todos."}
                </span>
              </div>
              <button
                type="button"
                className={styles.expandClose}
                onClick={() => setDrawer(null)}
                aria-label="Schließen"
                style={{ color: BRAND.white }}
              >
                <span style={{ width: 20, height: 20, display: "inline-flex" }}>
                  <Icons.close />
                </span>
              </button>
            </div>

            <div className={styles.drawerBody}>
              {(() => {
                const filtered =
                  drawer === "vorgaenge"
                    ? riskAssignedVorgaenge
                        .map((v) => ({
                          ...v,
                          todos: v.todos.filter((t) => isOpenStatus(t.status)),
                        }))
                        .filter((v) => v.todos.length > 0)
                    : riskAssignedVorgaenge
                        .map((v) => ({
                          ...v,
                          todos: v.todos.filter(
                            (t) => isOpenStatus(t.status) && isOverdueOrDue(t.twv)
                          ),
                        }))
                        .filter((v) => v.todos.length > 0);

                if (filtered.length === 0) {
                  return (
                    <div className={styles.emptyDrawer}>
                      {drawer === "vorgaenge"
                        ? "Keine offenen Vorgänge mit Risiko-Zuordnung."
                        : "Keine fälligen Wiedervorlagen mit Risiko-Zuordnung."}
                    </div>
                  );
                }

                return filtered.map((v) => (
                  <div key={v.id} className={styles.drawerCard}>
                    <div className={styles.drawerVorgang}>
                      <span style={{ width: 18, height: 18, display: "inline-flex", color: BRAND.purple }}>
                        <Icons.inbox />
                      </span>
                      <span className={styles.drawerVorgangTitle}>{v.titel}</span>
                      <span className={styles.drawerVorgangMeta}>
                        {v.fortschrittErledigt}/{v.fortschrittGesamt}
                      </span>
                    </div>
                    {v.todos.map((t, idx) => {
                      const statusColor =
                        t.status === "Erledigt"
                          ? BRAND.pillarGreen
                          : isOverdueOrDue(t.twv)
                          ? "#d54545"
                          : BRAND.gold;
                      const linkedRisks = (t.assignedRisks || []).filter((ar) =>
                        riskIdSet.has(ar.id)
                      );
                      return (
                        <div
                          key={t.id}
                          className={mergeClasses(
                            styles.drawerTodo,
                            idx === 0 && styles.drawerTodoFirst
                          )}
                        >
                          <span
                            className={styles.todoStatus}
                            style={{ backgroundColor: statusColor }}
                          />
                          <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                            <span className={styles.todoTitle}>{t.titel}</span>
                            <span className={styles.todoMeta}>
                              TWV {fmtDate(t.twv)} · Frist {fmtDate(t.frist)}
                              {linkedRisks.length > 0 && (
                                <>
                                  {" · "}
                                  {linkedRisks
                                    .map((ar) => {
                                      const r = realRisks.find((rr) => rr.id === ar.id);
                                      return r ? risikoobjektLabel(r) : `Risiko #${ar.id}`;
                                    })
                                    .join(", ")}
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          </aside>
        </div>
      )}


    </div>
  );
};

export default ContactRisksPanel;
